import React, { useEffect, useMemo, useState } from "react";
import { List, ListItem, ListItemText, Divider, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { generateDirStyles } from "orion-components/SharedComponents/GenerateDirStyles/GenerateDirStyles";
import UserAvatar from "orion-components/UserAvatar";
import { SearchField } from "orion-components/CBComponents";
import { Translate, getTranslation } from "orion-components/i18n";

import { startOnlineUsersStream, stopOnlineUsersStream } from "./Redux/actions";

const baseStyles = {
	container: {
		paddingRight: 10,
		paddingLeft: 15,
		height: "100%",
		display: "flex",
		flexDirection: "column"
	},
	header: {
		padding: "16px 0 8px 0",
		color: "#FFFFFF",
		display: "flex",
		alignItems: "center",
		gap: 8
	},
	headerDot: {
		width: 8,
		height: 8,
		borderRadius: "50%",
		backgroundColor: "#4CAF50"
	},
	headerTitle: {
		fontSize: 14
	},
	searchWrap: {
		padding: "0 0 12px 0"
	},
	divider: {
		backgroundColor: "rgba(255, 255, 255, 0.1)"
	},
	listWrap: {
		flex: 1,
		overflowY: "auto"
	},
	listItem: {
		backgroundColor: "transparent"
	},
	avatar: {
		marginRight: 16
	},
	primaryTextProps: {
		style: { color: "#fff", fontSize: 14 }
	},
	secondaryLine: {
		color: "rgba(255,255,255,0.7)",
		display: "block",
		fontSize: 13
	},
	secondaryEmail: {
		color: "rgba(255,255,255,0.55)",
		display: "block",
		fontSize: 13
	},
	emptyState: {
		padding: 16,
		color: "rgba(255,255,255,0.7)"
	}
};

const OnlineUsers = () => {
	const dispatch = useDispatch();
	const dir = useSelector(state => getDir(state));
	const styles = generateDirStyles(baseStyles, dir);

	const usersById = useSelector(state => state?.appState?.dock?.onlineUsers?.usersById || {});
	const users = Object.values(usersById);

	const [query, setQuery] = useState("");

	useEffect(() => {
		dispatch(startOnlineUsersStream());
		return () => dispatch(stopOnlineUsersStream());
	}, [dispatch]);

	const filteredUsers = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return users;

		return users.filter(u => {
			const name = (u.name || "").toLowerCase();
			const email = (u.email || "").toLowerCase();
			const roles = Array.isArray(u.roleTitles) ? u.roleTitles.join(" ").toLowerCase() : "";
			const workstation = (u.workstation?.name || u.workstation?.id || "").toLowerCase();

			return name.includes(q) || email.includes(q) || roles.includes(q) || workstation.includes(q);
		});
	}, [users, query]);

	return (
		<div style={styles.container}>
			<div style={styles.header}>
				<span style={styles.headerDot} />
				<Typography style={styles.headerTitle}>
					<Translate value="global.dock.onlineUsers.title" /> (
					<Translate value="global.dock.onlineUsers.onlineCount" count={users.length} />)
				</Typography>
			</div>

			<div style={styles.searchWrap}>
				<SearchField
					key="online-user-search"
					value={query}
					placeholder={getTranslation("global.dock.onlineUsers.searchPlaceholder", {}, dir)}
					handleChange={e => setQuery(e.target.value)}
					handleClear={() => setQuery("")}
					dir={dir}
					autoFocus={true}
				/>
			</div>

			<Divider style={styles.divider} />

			<div style={styles.listWrap}>
				<List disablePadding>
					{filteredUsers.map(u => {
						const roleTitles = Array.isArray(u.roleTitles) ? u.roleTitles.join(", ") : "";
						const workstation = u.workstation?.name || u.workstation?.id || "";

						return (
							<div key={u.id}>
								<ListItem sx={styles.listItem}>
									<UserAvatar user={u} size={65} style={styles.avatar} />

									<ListItemText
										primary={u.name || u.email || u.id}
										primaryTypographyProps={styles.primaryTextProps}
										secondary={
											<>
												<Typography component="span" sx={styles.secondaryLine}>
													<Translate value="global.dock.onlineUsers.role" />:{" "}
													{[roleTitles].filter(Boolean).join(" • ")}
												</Typography>

												{workstation !== "" && (
													<Typography component="span" sx={styles.secondaryLine}>
														<Translate value="global.dock.onlineUsers.workstation" />:{" "}
														{workstation}
													</Typography>
												)}

												<Typography component="span" sx={styles.secondaryEmail}>
													{u.email}
												</Typography>
											</>
										}
									/>
								</ListItem>

								<Divider style={styles.divider} />
							</div>
						);
					})}

					{filteredUsers.length === 0 && (
						<Typography style={styles.emptyState}>
							<Translate value="global.dock.onlineUsers.noMatches" />
						</Typography>
					)}
				</List>
			</div>
		</div>
	);
};

export default OnlineUsers;
